import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HubFeature, PortalType } from '@ats/api/cm';
import { CmAuthService, CmFeatureService } from '@ats/cm/core';
import { SettingsService } from '@cm/core/settings';
import { Observable, combineLatest, filter, map, of, shareReplay, switchMap, take, withLatestFrom } from 'rxjs';
import { IngressType } from '../models/ingress';

@Injectable({
	providedIn: 'root',
})
export class IngressService {
	type$: Observable<IngressType>;
	constructor(
		private readonly router: Router,
		private readonly features: CmFeatureService,
		private readonly settings: SettingsService,
		private readonly auth: CmAuthService
	) {
		this.type$ = this.features.public$.pipe(
			filter(features => !!features),
			map(features =>
				this.settings.portal === PortalType.CandidatePortal
					? this.getCpIngressType(features)
					: this.getSpIngressType(features)
			),
			shareReplay(1)
		);
	}

	routeToLogin(): void {
		const internalGuard$ = this.auth.guard$.pipe(map(guard => guard.reset().isInternal().all));

		const internal$ = this.auth.token$.pipe(switchMap(token => (!token ? of(false) : internalGuard$)));

		combineLatest([this.settings.client$, this.settings.cultureCode$, internal$])
			.pipe(withLatestFrom(this.type$), take(1))
			.subscribe(([[client, culture, isInternal], ingressType]) => {
				if (ingressType === 'login') {
					this.router.navigate([client, culture, 'login']);
				} else {
					let redirectUri = `${this.settings.env.apiUrl}/${client}`;
					if (this.settings.portal === PortalType.SponsorPortal) {
						redirectUri += isInternal ? '/internal' : '/sponsor';
					}
					redirectUri += `/sso/ingress/${ingressType}`;
					location.href = redirectUri;
				}
			});
	}

	private getCpIngressType(features: string[]): IngressType {
		if (features.includes(HubFeature.CandidateAccessIntegrationIngressOidc)) {
			return 'oidc';
		} else if (features.includes(HubFeature.CandidateAccessIntegrationIngressJwt)) {
			return 'jwt';
		} else if (features.includes(HubFeature.CandidateAccessIntegrationIngressWsTrust)) {
			return 'wstrust';
		}
		return 'login';
	}

	private getSpIngressType(features: string[]): IngressType {
		if (features.includes(HubFeature.UserAccessIntegrationOidc)) {
			return 'oidc';
		} else if (features.includes(HubFeature.UserAccessIntegrationJwt)) {
			return 'jwt';
		} else if (features.includes(HubFeature.UserAccessIntegrationWsTrust)) {
			return 'wstrust';
		}
		return 'login';
	}
}
